/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, X, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Two distinct groups per v260.54 product decision:
//   • Workflow    — auto-derived by the approval system; choosing one of
//                   these manually forces an override that beats the
//                   derived state until cleared.
//   • Lifecycle   — pure label / side-effect states (won/lost/archived
//                   trigger archival + winner side-effects on the BE).
export const STATUS_GROUPS = [
  {
    name: 'Workflow',
    options: [
      { value: 'draft', label: 'Draft', tone: 'bg-slate-100 text-slate-700' },
      { value: 'in_review', label: 'In Review', tone: 'bg-amber-100 text-amber-800' },
      { value: 'approved', label: 'Approved', tone: 'bg-emerald-100 text-emerald-800' },
      { value: 'changes_requested', label: 'Changes Requested', tone: 'bg-rose-100 text-rose-800' },
    ],
  },
  {
    name: 'Lifecycle',
    options: [
      { value: 'confirmed', label: 'Confirmed', tone: 'bg-indigo-100 text-indigo-800' },
      { value: 'won', label: 'Won', tone: 'bg-emerald-200 text-emerald-900' },
      { value: 'lost', label: 'Lost', tone: 'bg-slate-200 text-slate-700' },
      { value: 'completed', label: 'Completed', tone: 'bg-teal-100 text-teal-800' },
      { value: 'archived', label: 'Archived', tone: 'bg-slate-300 text-slate-800' },
    ],
  },
];

const ALL_OPTIONS = STATUS_GROUPS.flatMap(g => g.options);

export function statusLabel(value) {
  const opt = ALL_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : (value || '—');
}

export function statusTone(value) {
  const opt = ALL_OPTIONS.find(o => o.value === value);
  return opt ? opt.tone : 'bg-slate-100 text-slate-600';
}

/**
 * Shared status setter chip. Renders as a clickable badge that pops a
 * grouped menu (Workflow / Lifecycle) with a "Clear override" footer.
 *
 * Props:
 *  - estimateId, currentStatus
 *  - overrideBy, overrideAt — optional metadata to show in the helper line
 *  - onChange(newStatus) — fired AFTER successful PATCH so the parent can
 *      refetch / update local state
 *  - testIdPrefix, compact (smaller chip)
 *  - disabled
 */
export default function EstimateStatusSelect({
  estimateId,
  currentStatus,
  overrideBy,
  overrideAt,
  onChange,
  testIdPrefix = 'estimate-status',
  compact = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const applyStatus = async (newStatus) => {
    if (saving) return;
    setSaving(true);
    try {
      const r = await axios.patch(
        `${API_URL}/api/estimates/${estimateId}/workflow-status`,
        { status: newStatus || null },
      );
      const label = newStatus ? statusLabel(newStatus) : 'auto-derived';
      toast.success(newStatus ? `Status set to ${label}` : 'Override cleared');
      setOpen(false);
      onChange && onChange(r.data);
    } catch (e) {
      logger.warn('workflow-status PATCH failed', e);
      toast.error(e?.response?.data?.detail || 'Could not update status');
    } finally {
      setSaving(false);
    }
  };

  const tone = statusTone(currentStatus);
  const label = statusLabel(currentStatus);
  const isOverride = Boolean(overrideBy && overrideAt);
  const overrideAtLabel = overrideAt
    ? new Date(overrideAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testIdPrefix}-trigger`}
          className={`inline-flex items-center gap-1 ${tone} ${compact ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} rounded-md border border-transparent hover:border-slate-300 transition disabled:cursor-not-allowed disabled:opacity-60`}
          title={isOverride ? `Overridden by ${overrideBy} on ${overrideAtLabel}. Click to change.` : 'Click to override status'}
        >
          <span className="font-medium">{label}</span>
          {isOverride && <Info className="w-2.5 h-2.5 opacity-60" />}
          <ChevronDown className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start" data-testid={`${testIdPrefix}-menu`}>
        {STATUS_GROUPS.map((g) => (
          <div key={g.name} className="border-b border-slate-100 last:border-b-0">
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-400">{g.name}</div>
            <div className="pb-1.5">
              {g.options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => applyStatus(opt.value)}
                  disabled={saving}
                  data-testid={`${testIdPrefix}-option-${opt.value}`}
                  className="w-full text-left px-3 py-1.5 hover:bg-slate-50 text-xs flex items-center gap-2"
                >
                  <span className={`inline-block w-2 h-2 rounded-sm ${opt.tone}`} />
                  <span className="text-slate-700">{opt.label}</span>
                  {currentStatus === opt.value && <span className="ml-auto text-[10px] text-emerald-600">current</span>}
                </button>
              ))}
            </div>
          </div>
        ))}
        {isOverride && (
          <div className="px-3 py-2 border-t border-slate-100 bg-amber-50/40">
            <div className="text-[10px] text-amber-800 leading-tight mb-1.5">
              Overridden by <strong>{overrideBy}</strong> on {overrideAtLabel}
            </div>
            <button
              type="button"
              onClick={() => applyStatus(null)}
              disabled={saving}
              data-testid={`${testIdPrefix}-clear`}
              className="w-full inline-flex items-center justify-center gap-1 text-[11px] text-rose-700 hover:bg-rose-50 px-2 py-1 rounded"
            >
              <X className="w-3 h-3" />
              Clear override (revert to auto)
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
